import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock, Loader2 } from 'lucide-react';
import AdminDashboard from './AdminDashboard';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'vetrich_admin_token';

const Admin = () => {
  const { toast } = useToast();
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [checking, setChecking] = useState(!!token);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    (async () => {
      try {
        await axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
      } finally {
        setChecking(false);
      }
    })();
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast({ title: 'Welcome back', description: 'You are now signed in.' });
    } catch (err) {
      toast({ title: 'Login failed', description: 'Invalid email or password.' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0d1e33]">
        <Loader2 className="animate-spin text-[#c9a227]" size={32} />
      </div>
    );
  }

  if (token) return <AdminDashboard token={token} onLogout={handleLogout} />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0d1e33] px-5">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <img src="/vetrich-logo.png" alt="Vetrich" className="h-12 w-auto" />
          <span className="font-display font-bold text-white text-xl tracking-tight">VETRICH TECHNOLOGIES</span>
        </div>
        <form onSubmit={handleLogin} className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-9 h-9 rounded-lg bg-[#12263f] flex items-center justify-center"><Lock size={16} className="text-[#c9a227]" /></span>
            <h1 className="font-display font-bold text-[#12263f] text-xl">Admin Login</h1>
          </div>
          <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com"
            className="w-full px-4 py-3 rounded-lg border border-gray-200 mb-4 text-[15px] focus:outline-none focus:border-[#12263f] focus:ring-1 focus:ring-[#12263f]" />
          <label className="block text-sm font-medium text-gray-600 mb-1">Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••"
            className="w-full px-4 py-3 rounded-lg border border-gray-200 mb-6 text-[15px] focus:outline-none focus:border-[#12263f] focus:ring-1 focus:ring-[#12263f]" />
          <button type="submit" disabled={loading} className="w-full py-3.5 rounded-lg bg-[#12263f] text-white font-semibold hover:bg-[#1c3a5e] transition-all disabled:opacity-70 inline-flex items-center justify-center gap-2">
            {loading && <Loader2 size={18} className="animate-spin" />}
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <p className="text-center text-white/40 text-xs mt-6">Vetrich Technologies · Content Management</p>
      </div>
    </div>
  );
};

export default Admin;
